import {InputSlider} from '#/atoms/input-slider';
import {SingleTokenPriceInput} from '#/atoms/single-token-price-input';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {ActionMenuOverviewInfoRow} from '#/components/actionmenu/action-menu-overview';
import {ActionMenuContentSectionOverview} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {MessageCase} from '#/components/actionmenu/types';
import AsyncButton from '#/components/async-button/component';
import {WalletContext} from '#/contexts/wallet-provider';
import {useStableTokenBalance} from '#/hooks/query/use-stable-token-balance';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {useCoinDisplayPreferences, useCoinDisplayTypes} from '#/hooks/use-coin-display-preferences';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {getBorrowCollateralPositionStyling} from '#/utils/borrow-utils';
import {minimumTransactionFees} from '#/utils/contract-consts';
import {stringToTokenAmount, tokenAmountToInputString} from '#/utils/token-string-utils';
import {DefaultChildren} from '#/utils/utils';
import {
  TransactionType,
  collateralRatioFor,
  collateralRatioMinimumPercentage,
  necessaryCollateral,
} from '@shareland/shareland-common';
import React, {FormEvent, FunctionComponent, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

enum CollateralPositionVariant {
  OPEN = 'open',
  ADJUST = 'adjust',
}

enum FormActions {
  OPEN = 'open',
  ADJUST = 'adjust',
  CLOSE = 'close',
}

export const ActionMenuTradeBorrowTab: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {land, landMarketInfo, landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const {t} = useTranslation<'translation'>('translation');
  const displayPref = useCoinDisplayPreferences();
  const {coinDisplayTypes} = useCoinDisplayTypes();
  const [showInfoTile, onCloseTile] = useDismissableInfoTile('Borrow');

  // Internal hooks
  const [landTokenAmount, setLandTokenAmount] = useState(landAccountInfo.borrowedLandTokenAmount);
  const [stableTokenAmount, setStableTokenAmount] = useState(landAccountInfo.stableTokenInCollateralAmount);
  const [landTokenInput, setLandTokenInput] = useState(tokenAmountToInputString(landTokenAmount));
  const [stableTokenInput, setStableTokenInput] = useState(
    tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(stableTokenAmount), 2),
  );
  const [collateralPercentage, setCollateralPercentage] = useState(
    collateralRatioFor({landTokenAmount, stableTokenAmount, oraclePrice: landMarketInfo.oraclePrice}),
  );

  const collateralPositionVariant =
    landAccountInfo.borrowedLandTokenAmount === 0 ? CollateralPositionVariant.OPEN : CollateralPositionVariant.ADJUST;

  // Vars - Global
  const transactionFee = minimumTransactionFees;
  const totalStableAmount = stableTokenAmount + transactionFee;

  const landAmountPositionDifference = landTokenAmount - landAccountInfo.borrowedLandTokenAmount;
  const stableAmountPositionDifference = stableTokenAmount - landAccountInfo.stableTokenInCollateralAmount;

  // Vars - Open
  const transactionVarsOpen: TransactionVariables[TransactionType.OpenCollateralPosition] = {
    landTokenAmount,
    stableTokenAmount,
    transactionFee,
    totalStableAmount,
    collateralRatio: collateralPercentage,
  };

  // Vars - Adjust
  const transactionVarsAdjust: TransactionVariables[TransactionType.AdjustCollateralPosition] = {
    ...transactionVarsOpen,
    stableAmountPositionDifference,
  };

  const noticeMessagesOpenAdjust = useOpenAdjustPositionStatusMessage(transactionVarsAdjust);

  const canStartTransactionFlowOpenAdjust =
    (landAmountPositionDifference !== 0 || stableAmountPositionDifference !== 0) &&
    stableTokenAmount !== 0 &&
    noticeMessagesOpenAdjust.every(elem => elem.canSubmitForm);

  // Vars - Close
  const transactionVarsClose: TransactionVariables[TransactionType.CloseCollateralPosition] = {
    borrowedLandTokenAmount: landAccountInfo.borrowedLandTokenAmount,
    stableTokenInCollateralAmount: landAccountInfo.stableTokenInCollateralAmount,
  };

  const noticeMessagesClose = useClosePositionStatusMessage();
  const canStartTransactionFlowClose = noticeMessagesClose.every(elem => elem.canSubmitForm);
  const collateralRatioEnabled = landTokenAmount > 0 && stableTokenAmount > 0;

  // Functions
  const onLandInputChange = (amount: string) => {
    setLandTokenInput(amount);
    const newLandTokenAmount = stringToTokenAmount(amount);
    setLandTokenAmount(newLandTokenAmount);

    if (collateralPositionVariant === CollateralPositionVariant.OPEN) {
      const newStableTokenAmount = necessaryCollateral(newLandTokenAmount, landMarketInfo, collateralPercentage / 100);
      setStableTokenAmount(newStableTokenAmount);

      const stableTokenAmountAdjustedForUSD = displayPref.usdcToNumericDisplayAmount(newStableTokenAmount);
      setStableTokenInput(tokenAmountToInputString(stableTokenAmountAdjustedForUSD, 2));
    } else {
      const newCollateralPercentage = collateralRatioFor({
        landTokenAmount: newLandTokenAmount,
        stableTokenAmount,
        oraclePrice: landMarketInfo.oraclePrice,
      });
      setCollateralPercentage(newCollateralPercentage);
    }
  };
  const onStableInputChange = (amount: string) => {
    setStableTokenInput(amount);

    const newStableTokenAmount = stringToTokenAmount(amount);
    const stableTokenAmountAdjustedForUSD = displayPref.numericDisplayAmountToUsdc(newStableTokenAmount);
    setStableTokenAmount(stableTokenAmountAdjustedForUSD);

    const newCollateralPercentage = collateralRatioFor({
      landTokenAmount,
      stableTokenAmount: stableTokenAmountAdjustedForUSD,
      oraclePrice: landMarketInfo.oraclePrice,
    });
    setCollateralPercentage(newCollateralPercentage);
  };
  const onCollateralPercentageChange = (newCollateralPercentage: number) => {
    setCollateralPercentage(newCollateralPercentage);

    const newStableTokenAmount = necessaryCollateral(landTokenAmount, landMarketInfo, newCollateralPercentage / 100);
    setStableTokenAmount(newStableTokenAmount);

    const stableTokenAmountAdjustedForUSD = displayPref.usdcToNumericDisplayAmount(newStableTokenAmount);
    setStableTokenInput(tokenAmountToInputString(stableTokenAmountAdjustedForUSD, 2));
  };

  const openPosition = async (event: FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlowOpenAdjust) return;

    initiateTransaction({
      type: TransactionType.OpenCollateralPosition,
      data: transactionVarsOpen,
    });
  };

  const adjustPosition = async (event: FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlowOpenAdjust) return;

    initiateTransaction({
      type: TransactionType.AdjustCollateralPosition,
      data: transactionVarsAdjust,
    });
  };

  const closePosition = async () => {
    if (!canStartTransactionFlowClose) return;

    initiateTransaction({
      type: TransactionType.CloseCollateralPosition,
      data: transactionVarsClose,
    });
  };

  const onStartTransaction = useBlockingLoginAction(async (event: React.FormEvent) => {
    const nativeEvent = event.nativeEvent as any;
    const targetFormAction = nativeEvent?.submitter?.name;

    switch (targetFormAction) {
      case FormActions.OPEN:
        openPosition(event);
        break;
      case FormActions.ADJUST:
        adjustPosition(event);
        break;
      case FormActions.CLOSE:
        closePosition();
        break;
    }
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    onStartTransaction(event);
  };

  useEffect(() => {
    setStableTokenInput(tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(stableTokenAmount), 2));
  }, [coinDisplayTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessagesOpenAdjust.concat(noticeMessagesClose)}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={'Borrow'} />,
          grow: (
            <>
              <SingleTokenPriceInput
                className='mb-4'
                title={t('pages.actionMenu.trade.sections.borrow.landTokenInputTitle', {
                  context: collateralPositionVariant === CollateralPositionVariant.OPEN ? 'open' : 'adjust',
                })}
                tokenName={land.tokenSymbol}
                tokenAmount={landTokenInput}
                onChange={onLandInputChange}
              />
              <SingleTokenPriceInput
                title={t('pages.actionMenu.trade.sections.borrow.stableTokenInputTitle', {
                  context: collateralPositionVariant === CollateralPositionVariant.OPEN ? 'open' : 'adjust',
                })}
                tokenName={displayPref.symbol}
                tokenAmount={stableTokenInput}
                onChange={onStableInputChange}
              />
              <AdjustPositionSliderCompound
                enabled={collateralRatioEnabled}
                value={collateralPercentage}
                onChange={onCollateralPercentageChange}
              />
            </>
          ),

          overview: (
            <ActionMenuContentSectionOverview>
              <ActionMenuOverviewInfoRow.Borrow landTokenAmount={transactionVarsAdjust.landTokenAmount} />
              <ActionMenuOverviewInfoRow.Deposit stableTokenAmount={transactionVarsAdjust.stableTokenAmount} />
              <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVarsAdjust.transactionFee} />
              <ActionMenuOverviewInfoRow.Total total={transactionVarsAdjust.totalStableAmount} />
              <ActionMenuOverviewInfoRow.CollateralRatio
                collateralRatio={collateralRatioEnabled ? transactionVarsAdjust.collateralRatio : undefined}
              />
            </ActionMenuContentSectionOverview>
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        {collateralPositionVariant === CollateralPositionVariant.OPEN ? (
          <>
            <ActionMenuTransactionReviewButton name={FormActions.OPEN} disabled={!canStartTransactionFlowOpenAdjust} />
          </>
        ) : (
          <>
            <AsyncButton
              name={FormActions.CLOSE}
              type='submit'
              variant='hollow'
              disabled={!canStartTransactionFlowClose}
            >
              {t('pages.actionMenu.trade.sections.borrow.ctaBottomButtonClose')}
            </AsyncButton>
            <AsyncButton
              name={FormActions.ADJUST}
              type='submit'
              variant='regular'
              disabled={!canStartTransactionFlowOpenAdjust}
            >
              {t('pages.actionMenu.trade.sections.borrow.ctaBottomButtonAdjust')}
            </AsyncButton>
          </>
        )}
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const AdjustPositionSliderCompound: FunctionComponent<{
  enabled: boolean;
  value: number;
  onChange: (value: number) => void;
}> = ({enabled, value, onChange}) => {
  const {t} = useTranslation('translation');

  const style = getBorrowCollateralPositionStyling(value);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.parseInt(e.target.value);
    onChange(newValue);
  };

  return (
    <div className={`flex mt-4 gap-6 items-center ${!enabled ? 'grayscale pointer-events-none' : ''}`}>
      <div className='whitespace-nowrap text-gray-400 text-sm pr-3 border-r border-gray-400'>
        {t('pages.actionMenu.trade.sections.borrow.adjustCollateralRatio')}
      </div>

      <InputSlider min={111} max={150} step={1} value={value} onChange={onInputChange} disabled={!enabled} />

      <input
        type='number'
        value={enabled ? value : 0}
        onChange={onInputChange}
        className={`font-bold text-center ${style.bg} text-white outline-none p-1 text-sm rounded-lg w-[80px]`}
      />
    </div>
  );
};

const useClosePositionStatusMessage = () => {
  const {land, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');

  const cases: Record<string, MessageCase> = {
    borrowedLandAmountIsAboveOwnedLandAmount: {
      condition: () => landAccountInfo.borrowedLandTokenAmount > landAccountInfo.landTokenAmount,
      message: () =>
        actionMenuMessageContent.needMoreLandTokenToCloseCollateralPosition(
          t,
          land.tokenSymbol,
          landAccountInfo.borrowedLandTokenAmount - landAccountInfo.landTokenAmount,
        ),
    },
  };

  return useMessageCasesToContent(cases);
};

export const useOpenAdjustPositionStatusMessage = (
  transactionVars: TransactionVariables[TransactionType.AdjustCollateralPosition],
) => {
  const {land, landAccountInfo} = useContext(ActionMenuContext);
  const {
    accountResult: {isConnected},
  } = useContext(WalletContext);
  const {t} = useTranslation('translation');
  const {data: stableTokenBalance} = useStableTokenBalance();

  const hasInputValueChanged =
    transactionVars.landTokenAmount !== landAccountInfo.borrowedLandTokenAmount ||
    transactionVars.stableTokenAmount !== landAccountInfo.stableTokenInCollateralAmount;

  const requiredStableTokenAmount = transactionVars.stableTokenAmount - landAccountInfo.stableTokenInCollateralAmount;
  const requiredLandTokenAmount = landAccountInfo.borrowedLandTokenAmount - transactionVars.landTokenAmount;

  const cases: Record<string, MessageCase> = {
    notEnoughLandTokenToAdjustPosition: {
      condition: () => requiredLandTokenAmount > landAccountInfo.landTokenAmount,
      message: () =>
        actionMenuMessageContent.tokenAmountRequiredForAction(t, requiredLandTokenAmount, land.tokenSymbol),
    },
    ...(isConnected && {
      notEnoughStableTokenToAdjustPosition: {
        condition: () =>
          requiredStableTokenAmount > 0 &&
          requiredStableTokenAmount + transactionVars.transactionFee > Number(stableTokenBalance),
        message: () => actionMenuMessageContent.notEnoughUsdc(t),
      },
    }),
    collateralIsTooLow: {
      condition: () => transactionVars.collateralRatio <= collateralRatioMinimumPercentage,
      message: () => actionMenuMessageContent.collateralRatioBelowMinimum(t, transactionVars.collateralRatio),
    },
    collateralIsBelowSuggestion: {
      condition: () => transactionVars.collateralRatio < collateralRatioMinimumPercentage,
      message: () =>
        actionMenuMessageContent.collateralRatioBelowSuggestion(
          t,
          transactionVars.collateralRatio,
          hasInputValueChanged,
        ),
    },
  };

  return useMessageCasesToContent(cases, {
    usdcAmount: transactionVars.stableTokenAmount,
  });
};
